import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import {
  sendTestMaster,
  type EmailTemplateMaster,
  type SendTestResult,
} from '../api/platform-email-template.api';

interface Props {
  open: boolean;
  template: EmailTemplateMaster;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Send-test dialog (COMMUNICATION_DESIGN.md §6 / §8.2). Sends a real email. */
export function SendTestDialog({ open, template, onClose }: Props) {
  const [recipient, setRecipient] = useState('');
  const [sampleData, setSampleData] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecipient('');
      setSampleData('');
      setResult(null);
      setError(null);
    }
  }, [open]);

  const recipientValid = EMAIL_RE.test(recipient.trim());

  const handleSend = async () => {
    setError(null);
    setResult(null);

    let context: Record<string, unknown> | undefined;
    if (sampleData.trim()) {
      try {
        context = JSON.parse(sampleData) as Record<string, unknown>;
      } catch {
        setError('Sample data must be valid JSON.');
        return;
      }
    }

    setSending(true);
    try {
      setResult(await sendTestMaster(template.id, recipient.trim(), context));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Send test — ${template.name}`} size="md">
      <div className="space-y-4">
        <FormField label="Recipient email" htmlFor="send-test-recipient" required>
          <Input
            id="send-test-recipient"
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="you@example.com"
            disabled={sending}
          />
          {recipient.trim() && !recipientValid && (
            <p className="mt-1 text-[11px] text-destructive">Enter a valid email address.</p>
          )}
        </FormField>

        <FormField label="Sample data (optional JSON)" htmlFor="send-test-sample">
          <textarea
            id="send-test-sample"
            value={sampleData}
            onChange={(e) => setSampleData(e.target.value)}
            rows={4}
            disabled={sending}
            placeholder={'{\n  "customerName": "Acme Ltd"\n}'}
            className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Leave empty to render with the registry&rsquo;s sample values.
          </p>
        </FormField>

        {result && (
          <div
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
              result.success
                ? 'border-green-500/30 bg-green-500/10'
                : 'border-destructive/30 bg-destructive/5'
            }`}
          >
            <FontAwesomeIcon
              icon={result.success ? faCircleCheck : faTriangleExclamation}
              className={`mt-0.5 h-3.5 w-3.5 ${
                result.success ? 'text-green-600 dark:text-green-400' : 'text-destructive'
              }`}
            />
            <div>
              <p className="text-xs font-medium text-foreground">
                {result.success ? 'Test email dispatched' : 'Delivery failed'}
              </p>
              <p className="text-[11px] text-muted-foreground">{result.message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={sending}>
            Close
          </Button>
          <Button size="sm" onClick={() => void handleSend()} disabled={!recipientValid || sending}>
            {sending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
            )}
            Send test
          </Button>
        </div>
      </div>
    </Modal>
  );
}
